import {useMemo} from 'react';
import {StyleSheet} from 'react-native';
import {useTheme} from './ThemeContext';

/**
 * Turns a theme-aware style factory into a memoized `StyleSheet`.
 *
 * This is the single primitive every screen adopts to become dark-mode aware:
 * instead of a module-scope `StyleSheet.create({...})` frozen with light-only
 * hex, the screen declares its styles as a function of the active theme and
 * calls this hook. The resulting stylesheet is recomputed (and only then) when
 * the user flips light/dark, so colours follow `useTheme()` automatically while
 * layout stays identical.
 *
 * @example
 * const styles = useThemedStyles((theme, isDark) => ({
 *     screen: { flex: 1, backgroundColor: theme.colors.background },
 *     title:  { color: theme.colors.text },
 * }));
 *
 * @param {(theme: object, isDark: boolean) => Object.<string, object>} factory
 *   Builder receiving the active theme object (`theme.colors.*`, `theme.dark`)
 *   and the `isDark` boolean, returning a plain style map.
 * @returns {Object.<string, object>} A `StyleSheet.create` result, memoized on the theme.
 */
export const useThemedStyles = (factory) => {
    const {theme, isDark} = useTheme();

    return useMemo(() => StyleSheet.create(factory(theme, isDark)), [theme, isDark]);
};

export default useThemedStyles;
